import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  courseApi,
  Profile,
  IAssignedCourse,
  ITeamMembership,
  tPromiseFromFunctionResolved
} from 'common/modules/ReactShared';

interface IHomePageState {
  assignedCourses: IAssignedCourse[] | null;
  /** The schools the student belongs to, with their classes; null until fetched. */
  teamMemberships: ITeamMembership[] | null;
}

const initialState: IHomePageState = {
  assignedCourses: null,
  teamMemberships: null
};

const fetchTeamMemberships = createAsyncThunk<ITeamMembership[]>(
  'homePage/fetchTeamMemberships_request',
  async () => Profile.profileApi.getTeamMemberships()
);

const fetchAssignedCourses = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof courseApi.fetchAssignedCourses>
>('homePage/fetchAssignedCourses_request', async () => {
  const data = await courseApi.fetchAssignedCourses();
  return data;
});

const MAX_VISIBLE_NUMBER_OF_SLIDES = 9;
const fetchMyLessonsPreview = createAsyncThunk<
  Array<Profile.Lessons.Active.ILesson | Profile.Lessons.Completed.ILesson>,
  void
>('homePage/fetchMyLessonsPreview_request', async () => {
  const [activeLessonsRequest, completedLessonsRequest] = await Promise.allSettled([
    Profile.Lessons.Active.getLessons({
      per_page: MAX_VISIBLE_NUMBER_OF_SLIDES
    }),
    Profile.Lessons.Completed.getLessons({
      per_page: MAX_VISIBLE_NUMBER_OF_SLIDES
    })
  ]);

  let activeLessons: Array<Profile.Lessons.Active.ILesson> = [];
  let completedLessons: Array<Profile.Lessons.Completed.ILesson> = [];

  if (activeLessonsRequest.status === 'fulfilled') {
    activeLessons = activeLessonsRequest.value.lessons;
  }

  if (completedLessonsRequest.status === 'fulfilled') {
    completedLessons = completedLessonsRequest.value.lessons;
  }

  return [...activeLessons, ...completedLessons].slice(0, MAX_VISIBLE_NUMBER_OF_SLIDES);
});

const homePageSlice = createSlice({
  name: 'homePage',
  initialState: initialState as IHomePageState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAssignedCourses.fulfilled, (state, action) => {
      return {
        ...state,
        assignedCourses: action.payload.courses
      };
    });

    builder.addCase(fetchAssignedCourses.rejected, (state) => {
      return {
        ...state,
        assignedCourses: null
      };
    });

    builder.addCase(fetchTeamMemberships.fulfilled, (state, action) => {
      state.teamMemberships = action.payload;
    });

    // A failed lookup leaves the home as it was: no school card rather than a wrong one,
    // since a school without its classes shows no card.
    builder.addCase(fetchTeamMemberships.rejected, (state) => {
      state.teamMemberships = [];
    });
  }
});

const { reducer: homeReducer, actions } = homePageSlice;

const homeActions = {
  ...actions,
  fetchAssignedCourses,
  fetchMyLessonsPreview,
  fetchTeamMemberships
};

export { homeActions };
export default homeReducer;
export type { IHomePageState };
